/**
 * BrandLogo — die EINE wiederverwendbare Logo-Komponente (Issue #388).
 *
 * Vorher lag das Logo als kopiertes Markup an fünf Stellen (SiteNav 2×,
 * SiteFooter, DialogBrand, Styleguide) — mit drei verschiedenen Bildmarken und
 * dem Namen jedes Mal als Literal. Eine Umbenennung hätte man an jeder Stelle
 * einzeln nachziehen müssen; genau das war der Anlass.
 *
 * Der Name steht jetzt ausschließlich in BRAND_NAME. Schreibweise ändern =
 * eine Zeile.
 */

/** Wortmarke. Alex 2026-07-25: „unsere App heisst jetzt TSCHAPP, statt E mit A
 *  als Trick für App" — Tscheppa + APP verschmolzen. Das frühere ·AR-Suffix
 *  entfällt, der Name trägt die Pointe selbst. Schreibweise ändern = diese
 *  eine Zeile; sie speist Logo, Dialoge und alle Fließtexte. */
export const BRAND_NAME = 'TSCHAPP'

/** Bildmarke.
 *  - `asset`  — das weiße Logo-SVG aus /public (Overlays auf sicher dunklem Grund)
 *  - `inline` — schlichte Tropfen-Marke in currentColor (Footer: erbt die Textfarbe)
 *  - `color`  — die farbige Tscheppaschlucht-Marke (Header, Dialoge)
 *
 *  Zur Wahl zwischen `asset` und `inline` auf Flächen, deren Helligkeit
 *  wechselt: `inline` ist NICHT die Marke, sondern ein generischer Tropfen —
 *  sie löst zwar jedes Kontrastproblem (currentColor), gibt dafür aber die
 *  Wiedererkennung auf. Der Header war deshalb eine Zeit lang markenlos
 *  (Issue #394 → #419). `color` trägt auf hellem UND dunklem Grund und ist
 *  auf wechselnden Flächen die richtige Antwort; `inline` bleibt für den
 *  Footer, wo sie von Anfang an gestalterisch gemeint war.
 */
export type BrandMark = 'asset' | 'inline' | 'color'

/** Die geschlossene Marke: Tropfen UND Wortmarke in EINER Datei
 *  (`/logo-tschapp-*.svg`, Alex 2026-07-29). Das „A" in TSCHAPP ist dort der
 *  Tropfen selbst — Zeichen und Name sind gestaltet, nicht nebeneinander
 *  gesetzt, und genau deshalb darf der Aufrufer den Namen nicht zusätzlich
 *  danebenstellen. `hideName` wird für `lockup` erzwungen.
 *
 *  Die Buchstaben liegen als PFADE vor, nicht als Text: die Vorlage war in
 *  Montserrat gesetzt, und die Schrift lädt die App gar nicht — im Browser
 *  wäre eine Ersatzschrift eingesprungen (breiter, grotesk statt geometrisch,
 *  das letzte P lief aus dem Rahmen). Wer die Datei ersetzt: Text vorher in
 *  Pfade wandeln, sonst kommt der Fehler zurück.
 *
 *  EINFARBIG, nicht die bunte Fassung (Alex 2026-07-29: „ich hätte bitte
 *  gerne das schwarz weiss nicht die farbige version"). Umgesetzt als
 *  CSS-Maske statt als `<img>`: die Datei liefert nur noch die FORM, die
 *  Farbe kommt aus `currentColor`. Damit folgt die Marke automatisch der
 *  Textfarbe, die der Kopf ohnehin schon umschaltet — schwarz auf hellem
 *  Glas (`--light`), weiß über dem dunklen Hero. Die Alternative wäre
 *  gewesen, diese Bedingung in TypeScript nachzubauen; dann hätte man zwei
 *  Wahrheiten über dieselbe Frage, und die zweite wird irgendwann falsch.
 *
 *  Die farbige Fassung bleibt als Datei liegen (`logo-tschapp-farbe.svg`),
 *  wird aber nirgends mehr verwendet. */
const LOCKUP_MASK = '/logo-tschapp-schwarz.svg'
/** Seitenverhältnis der Marke (viewBox 222.59 × 54.12) — die Maske hat keine
 *  eigene Größe, die Breite muss aus der Höhe gerechnet werden. */
const LOCKUP_RATIO = 222.59 / 54.12

interface BrandLogoProps {
  /** Welche Bildmarke. Default `asset`. */
  mark?: BrandMark
  /** Geschlossene Marke (Zeichen + Wortmarke in einer Datei) statt
   *  Zeichen + separat gesetztem Namen. Schaltet die Wortmarke ab. */
  lockup?: boolean
  /** Wortmarke ausblenden (nur Zeichen). */
  hideName?: boolean
  /** Höhe der Bildmarke in px. Default richtet sich nach der Marke. */
  size?: number
  /** Klassen für den äußeren Container (Positionierung bleibt beim Aufrufer). */
  className?: string
  /** Klasse für die Wortmarke — damit bestehende Seiten ihre Typo behalten. */
  nameClassName?: string
  /** Klasse für die Bildmarke. */
  markClassName?: string
  style?: React.CSSProperties
}

/** Tropfen-Marke, erbt die Textfarbe — aus dem SiteFooter übernommen. */
function InlineMark({ size, className }: { size: number; className?: string }) {
  return (
    <svg width={size * 0.8} height={size} viewBox="0 0 64 80" fill="none" className={className} aria-hidden="true">
      <path d="M32 0C32 0 0 28 0 48C0 65.7 14.3 80 32 80C49.7 80 64 65.7 64 48C64 28 32 0 32 0Z" fill="currentColor" />
      <rect x="18" y="28" width="28" height="4" rx="2" fill="var(--ar-bg, #F7F5F0)" />
      <rect x="28" y="28" width="4" height="32" rx="2" fill="var(--ar-bg, #F7F5F0)" />
    </svg>
  )
}

export default function BrandLogo({
  mark = 'asset',
  lockup = false,
  hideName = false,
  size,
  className,
  nameClassName,
  markClassName,
  style,
}: BrandLogoProps) {
  const h = size ?? (mark === 'inline' ? 30 : 28)

  if (lockup) {
    /* Der Name steckt IM Bild. Ein maskiertes <span> ist für Vorleseprogramme
       nichts — deshalb `role="img"` + `aria-label`, damit der Name so ankommt
       wie beim alt-Text eines Bildes. */
    return (
      <span
        role="img"
        aria-label={BRAND_NAME}
        className={[className, markClassName].filter(Boolean).join(' ') || undefined}
        style={{
          display: 'inline-block',
          // Ohne Klasse die Maße selbst setzen; mit Klasse legt das CSS sie fest.
          ...(markClassName ? {} : { height: h, width: h * LOCKUP_RATIO }),
          backgroundColor: 'currentColor',
          WebkitMaskImage: `url(${LOCKUP_MASK})`,
          maskImage: `url(${LOCKUP_MASK})`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'left center',
          maskPosition: 'left center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          ...style,
        }}
      />
    )
  }

  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, ...style }}>
      {mark === 'inline' ? (
        <InlineMark size={h} className={markClassName} />
      ) : (
        <img
          src={mark === 'color' ? '/logo-tscheppa-farbe.svg' : '/logo-tscheppa-weiss.svg'}
          alt=""
          aria-hidden="true"
          className={markClassName}
          style={markClassName ? undefined : { height: h, width: 'auto' }}
        />
      )}
      {!hideName && <span className={nameClassName}>{BRAND_NAME}</span>}
    </span>
  )
}
